import { useMessage } from '@faceup/ui-base'
import type { BaseSyntheticEvent } from 'react'
import { useState } from 'react'
import type { FieldValues } from 'react-hook-form'
import { useFormLocalization } from '../FormLocalizationProvider'
import type { AfterSubmit, UseFormReturn } from '../wrappers'

type SubmitButtonText = 'save' | 'send' | 'add'

export type UseFormSubmitProps<FV extends FieldValues = FieldValues> = {
  form: UseFormReturn<FV>
  onSubmit: (values: FV) => boolean | Promise<boolean>
  afterSubmit?: AfterSubmit
  isSuccessMessageDisplayed?: boolean
  customSubmitSuccessText?: string
  submitButtonText?: SubmitButtonText
}

export const useFormSubmit = <FV extends FieldValues = FieldValues>({
  form,
  onSubmit,
  afterSubmit,
  isSuccessMessageDisplayed = true,
  customSubmitSuccessText,
  submitButtonText = 'send',
}: UseFormSubmitProps<FV>) => {
  const message = useMessage()
  const { successMessage } = useFormLocalization()
  const [isLoading, setIsLoading] = useState(false)

  const submit = async (values?: FV) => {
    setIsLoading(true)
    let submitValues = values
    if (!submitValues) {
      let resultVal: FV | undefined
      const mockEvent = {
        preventDefault: () => {
          // Mock event handler
        },
        stopPropagation: () => {
          // Mock event handler
        },
      } as unknown as BaseSyntheticEvent
      await form.handleSubmit((vals: FV) => {
        resultVal = vals
      })(mockEvent)
      submitValues = resultVal
    }
    if (!submitValues) {
      setIsLoading(false)
      return false
    }
    let result: boolean
    try {
      result = await onSubmit(submitValues)
    } catch {
      setIsLoading(false)
      return false
    }
    const resolvedAfterSubmit = (afterSubmit ?? form.afterSubmit) as AfterSubmit
    if (result) {
      switch (resolvedAfterSubmit) {
        case 'persistValues':
          form.reset(submitValues)
          break
        case 'resetValues':
          form.reset()
          break
      }
      if (isSuccessMessageDisplayed) {
        message.success(
          customSubmitSuccessText ?? successMessage[submitButtonText] ?? successMessage.send
        )
      }
    }
    setIsLoading(false)
    return result
  }

  return { submit, isLoading }
}
